import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Zap, Bell, BellRing, QrCode, Moon, CalendarDays } from "lucide-react";
import { Toaster, toast } from "sonner";
import { UserButton } from "@clerk/react";
import SchedulerForm from "@/components/SchedulerForm";
import ScheduleList from "@/components/ScheduleList";
import FlashOverlay from "@/components/FlashOverlay";
import InstallPWABanner from "@/components/InstallPWABanner";
import EditScheduleDialog from "@/components/EditScheduleDialog";
import ImportFlashDialog from "@/components/ImportFlashDialog";
import ShareAppDialog from "@/components/ShareAppDialog";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import WelcomeTour from "@/components/WelcomeTour";
import TemplateList from "@/components/TemplateList";
import ScheduleFromTemplateDialog from "@/components/ScheduleFromTemplateDialog";
import QuietHoursDialog from "@/components/QuietHoursDialog";
import CalendarSyncDialog from "@/components/CalendarSyncDialog";
import { useI18n } from "@/lib/i18n";
import {
  listSchedules,
  updateSchedule,
  createSchedule,
  listTemplates,
  getPreferences,
  syncCalendar,
} from "@/lib/api";
import { isInQuietWindow } from "@/lib/quietHours";
import { buildReminderMessage } from "@/lib/format";
import {
  notifyFlash,
  notificationPermission,
  requestNotificationPermission,
  supportsNotifications,
} from "@/lib/notifications";
import {
  isNative,
  syncNativeAlarms,
  registerNotificationTapHandler,
} from "@/lib/nativeAlarms";

export default function Dashboard() {
  const { t } = useI18n();
  const [schedules, setSchedules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFlash, setActiveFlash] = useState(null); // {message, soundEnabled, scheduleId?}
  const [now, setNow] = useState(() => new Date());
  const [notifPerm, setNotifPerm] = useState(() => notificationPermission());
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [showShareApp, setShowShareApp] = useState(false);
  const [schedulingTemplate, setSchedulingTemplate] = useState(null);
  const [showQuiet, setShowQuiet] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const snoozeCountsRef = useRef({}); // { [scheduleId]: int }
  const activeRef = useRef(activeFlash);
  activeRef.current = activeFlash;
  const prefsRef = useRef(preferences);
  prefsRef.current = preferences;

  const refresh = useCallback(async () => {
    try {
      const data = await listSchedules();
      setSchedules(data);
    } catch (e) {
      toast.error("FAILED TO LOAD SCHEDULES");
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshTemplates = useCallback(async () => {
    try {
      const data = await listTemplates();
      setTemplates(data);
    } catch (e) {
      // non-fatal
    }
  }, []);

  const refreshPreferences = useCallback(async () => {
    try {
      const p = await getPreferences();
      setPreferences(p);
    } catch (e) {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    refresh();
    refreshTemplates();
    refreshPreferences();
  }, [refresh, refreshTemplates, refreshPreferences]);

  // Auto-sync calendar on mount (if configured) and every 15 min while open.
  useEffect(() => {
    let cancelled = false;
    const doSync = async () => {
      try {
        const p = prefsRef.current;
        if (!p?.calendar_sync_enabled || !p?.calendar_configured) return;
        await syncCalendar();
        if (!cancelled) {
          refresh();
          refreshPreferences();
        }
      } catch {
        /* silent — manual Sync Now surfaces errors */
      }
    };
    // Initial sync shortly after prefs load
    const t0 = setTimeout(doSync, 1500);
    const iv = setInterval(doSync, 15 * 60 * 1000);
    return () => {
      cancelled = true;
      clearTimeout(t0);
      clearInterval(iv);
    };
  }, [refresh, refreshPreferences]);

  // Mirror backend schedules onto native OS alarms whenever list changes
  useEffect(() => {
    if (!isNative()) return;
    syncNativeAlarms(schedules).catch(() => {});
  }, [schedules]);

  // Handle notification tap on Android — pop the flash immediately
  useEffect(() => {
    if (!isNative()) return;
    const remove = registerNotificationTapHandler((extra) => {
      const s = schedules.find((x) => x.id === extra.scheduleId);
      if (!s) return;
      const message =
        extra.kind === "reminder" && s.reminder_minutes
          ? buildReminderMessage(s.message, s.reminder_minutes)
          : s.message;
      setActiveFlash({
        message,
        soundEnabled: s.sound_enabled,
        displayMode: s.display_mode || "fit",
        scheduleId: s.id,
        kind: extra.kind || "main",
      });
    });
    return remove;
  }, [schedules]);

  const handleEnableNotifs = useCallback(async () => {
    const res = await requestNotificationPermission();
    setNotifPerm(res);
    if (res === "granted") toast.success("NOTIFICATIONS ON");
    else if (res === "denied") toast.error("NOTIFICATIONS BLOCKED");
  }, []);

  // Tick every second: update clock + check due schedules
  useEffect(() => {
    const id = window.setInterval(() => {
      const current = new Date();
      setNow(current);

      if (activeRef.current) return; // one flash at a time

      const nowMs = current.getTime();

      // Check reminders first (anything whose reminder time has passed but reminder not yet fired)
      const dueReminder = schedules.find((s) => {
        if (s.triggered || s.reminder_triggered) return false;
        if (!s.reminder_minutes || s.reminder_minutes <= 0) return false;
        const mainMs = new Date(s.scheduled_at).getTime();
        const reminderMs = mainMs - s.reminder_minutes * 60 * 1000;
        return reminderMs <= nowMs && nowMs < mainMs;
      });

      if (dueReminder) {
        const reminderMsg = buildReminderMessage(dueReminder.message, dueReminder.reminder_minutes);
        const quietActive = isInQuietWindow(prefsRef.current, current);
        const tone =
          dueReminder.alarm_tone || (dueReminder.sound_enabled ? "beep" : "off");
        setActiveFlash({
          message: reminderMsg,
          alarmTone: quietActive ? "off" : tone,
          displayMode: dueReminder.display_mode || "fit",
          textSize: dueReminder.text_size || "md",
          bgColors: dueReminder.bg_colors || null,
          scheduleId: dueReminder.id,
          kind: "reminder",
          quietActive,
        });
        if (document.hidden && !quietActive) {
          notifyFlash({ title: "TimeFlash reminder", body: reminderMsg });
        }
        updateSchedule(dueReminder.id, { reminder_triggered: true })
          .then(() => refresh())
          .catch(() => {});
        return;
      }

      // Check main scheduled flashes
      const due = schedules.find(
        (s) => !s.triggered && new Date(s.scheduled_at).getTime() <= nowMs
      );
      if (due) {
        const quietActive = isInQuietWindow(prefsRef.current, current);
        const tone = due.alarm_tone || (due.sound_enabled ? "beep" : "off");
        setActiveFlash({
          message: due.message,
          alarmTone: quietActive ? "off" : tone,
          displayMode: due.display_mode || "fit",
          textSize: due.text_size || "md",
          bgColors: due.bg_colors || null,
          scheduleId: due.id,
          kind: "main",
          quietActive,
        });
        if (document.hidden && !quietActive) {
          notifyFlash({ title: "TimeFlash", body: due.message });
        }
        updateSchedule(due.id, { triggered: true })
          .then(() => refresh())
          .catch(() => {});
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [schedules, refresh]);

  const handleStopFlash = useCallback(() => {
    setActiveFlash(null);
  }, []);

  const handleSnoozeFlash = useCallback(
    async (minutes = 5) => {
      const current = activeRef.current;
      setActiveFlash(null);
      if (!current) return;
      // Track snooze count against the original schedule id
      const srcId = current.scheduleId;
      if (srcId) {
        snoozeCountsRef.current[srcId] = (snoozeCountsRef.current[srcId] || 0) + 1;
      }
      try {
        const newAt = new Date(Date.now() + minutes * 60 * 1000).toISOString();
        const src = schedules.find((s) => s.id === srcId);
        await createSchedule({
          message: src?.message || current.message,
          scheduled_at: newAt,
          sound_enabled: src?.sound_enabled ?? current.soundEnabled,
          display_mode: src?.display_mode ?? current.displayMode,
          reminder_minutes: null,
          repeat: "none",
        });
        toast.success(`SNOOZED ${minutes}M`);
        refresh();
      } catch (e) {
        toast.error("SNOOZE FAILED");
      }
    },
    [schedules, refresh]
  );

  const handleTriggerNow = useCallback((s) => {
    const quietActive = isInQuietWindow(prefsRef.current);
    const tone = s.alarm_tone || (s.sound_enabled ? "beep" : "off");
    setActiveFlash({
      message: s.message,
      alarmTone: quietActive ? "off" : tone,
      displayMode: s.display_mode || "fit",
      textSize: s.text_size || "md",
      bgColors: s.bg_colors || null,
      scheduleId: s.id,
      kind: "preview",
      quietActive,
    });
    toast(t("toast.previewing"));
  }, [t]);

  const handleFlashTemplateNow = useCallback((tpl) => {
    const quietActive = isInQuietWindow(prefsRef.current);
    setActiveFlash({
      message: tpl.message,
      alarmTone: quietActive ? "off" : (tpl.alarm_tone || "off"),
      displayMode: tpl.display_mode || "fit",
      textSize: tpl.text_size || "md",
      bgColors: tpl.bg_colors || null,
      scheduleId: `tpl-${tpl.id}`,
      kind: "preview",
      quietActive,
    });
    toast(t("toast.previewing"));
  }, [t]);

  const pendingCount = useMemo(
    () => schedules.filter((s) => !s.triggered).length,
    [schedules]
  );

  return (
    <div className="min-h-screen bg-[#F2F2F0] grain relative">
      <Toaster
        position="top-right"
        toastOptions={{
          className:
            "!bg-white !text-black !border-2 !border-black !rounded-none !shadow-[4px_4px_0_0_#050505] !font-mono",
        }}
      />

      {/* Header */}
      <header className="relative z-10 brutal-border border-l-0 border-r-0 border-t-0 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#FF3311] brutal-border w-10 h-10 flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" strokeWidth={3} />
            </div>
            <div>
              <div className="font-display text-xl md:text-2xl uppercase leading-none">
                {t("app.name")}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                {t("app.tagline")}
              </div>
            </div>
          </div>
          <div
            data-testid="live-clock"
            className="flex items-center gap-2 flex-wrap justify-end"
          >
            <LanguageSwitcher />
            <button
              data-testid="open-quiet-hours"
              onClick={() => setShowQuiet(true)}
              title={t("quiet.openTitle")}
              className={`inline-flex items-center gap-1 brutal-border px-2 py-2 font-mono text-[10px] uppercase tracking-widest brutal-shadow-sm brutal-interactive ${
                preferences?.quiet_enabled && isInQuietWindow(preferences, now)
                  ? "bg-[#0014C7] text-white"
                  : preferences?.quiet_enabled
                  ? "bg-[#E5FF00]"
                  : "bg-white"
              }`}
            >
              <Moon className="w-3 h-3" strokeWidth={3} />
              <span className="hidden md:inline">
                {preferences?.quiet_enabled && isInQuietWindow(preferences, now)
                  ? t("quiet.statusActive")
                  : t("quiet.title")}
              </span>
            </button>
            <button
              data-testid="open-calendar-sync"
              onClick={() => setShowCalendar(true)}
              title={t("cal.title")}
              className={`inline-flex items-center gap-1 brutal-border px-2 py-2 font-mono text-[10px] uppercase tracking-widest brutal-shadow-sm brutal-interactive ${
                preferences?.calendar_sync_enabled && preferences?.calendar_configured
                  ? "bg-[#00CCFF]"
                  : "bg-white"
              }`}
            >
              <CalendarDays className="w-3 h-3" strokeWidth={3} />
              <span className="hidden md:inline">
                {preferences?.calendar_sync_enabled && preferences?.calendar_configured
                  ? t("cal.statusOn")
                  : t("cal.openBtn")}
              </span>
            </button>
            {supportsNotifications() && notifPerm !== "granted" && notifPerm !== "unsupported" && (
              <button
                data-testid="enable-notifs"
                onClick={handleEnableNotifs}
                title={t("header.enableAlerts")}
                className="hidden sm:inline-flex items-center gap-1 bg-[#00CCFF] brutal-border px-2 py-2 font-mono text-[10px] uppercase tracking-widest brutal-shadow-sm brutal-interactive"
              >
                <Bell className="w-3 h-3" strokeWidth={3} /> {t("header.enableAlerts")}
              </button>
            )}
            {notifPerm === "granted" && (
              <span
                data-testid="notifs-on"
                className="hidden sm:inline-flex items-center gap-1 bg-[#E5FF00] brutal-border px-2 py-2 font-mono text-[10px] uppercase tracking-widest"
                title={t("header.alertsOn")}
              >
                <BellRing className="w-3 h-3" strokeWidth={3} /> {t("header.alertsOn")}
              </span>
            )}
            <span className="font-mono text-xs md:text-sm uppercase bg-black text-white brutal-border px-3 py-2">
              {now.toLocaleTimeString()} · {pendingCount} {t("header.pending")}
            </span>
            <button
              data-testid="share-app-btn"
              onClick={() => setShowShareApp(true)}
              title={t("header.shareApp")}
              className="bg-[#00CCFF] brutal-border w-10 h-10 flex items-center justify-center brutal-shadow-sm brutal-interactive"
            >
              <QrCode className="w-5 h-5" strokeWidth={3} />
            </button>
            <div className="brutal-border bg-white px-1" data-testid="user-button">
              <UserButton afterSignOutUrl="/" />
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-6">
        <div className="max-w-3xl">
          <div className="inline-block bg-[#E5FF00] brutal-border px-3 py-1 font-mono text-xs uppercase tracking-widest mb-5">
            {t("hero.badge")}
          </div>
          <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            {t("hero.line1")}
            <br />
            <span className="bg-black text-white px-2">{t("hero.line2")}</span>
            <br />
            {t("hero.line3")}
            <br />
            {t("hero.line4")}
          </h1>
          <p className="mt-5 font-mono text-sm md:text-base text-neutral-700 max-w-xl">
            {t("hero.body")}
          </p>
        </div>
      </section>

      {/* Bento grid */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-7">
            <SchedulerForm onCreated={refresh} onTemplateSaved={refreshTemplates} />
            <TemplateList
              templates={templates}
              onChanged={refreshTemplates}
              onFlashNow={handleFlashTemplateNow}
              onSchedule={(tpl) => setSchedulingTemplate(tpl)}
            />
          </div>
          <div className="lg:col-span-5">
            {loading ? (
              <div className="bg-white brutal-border brutal-shadow p-8 font-mono text-sm uppercase">
                Loading schedules...
              </div>
            ) : (
              <ScheduleList
                schedules={schedules}
                onChanged={refresh}
                onTriggerNow={handleTriggerNow}
                onEdit={(s) => setEditingSchedule(s)}
              />
            )}
          </div>
        </div>

        {/* Footer strip */}
        <div className="mt-10 brutal-border bg-black text-white px-5 py-3 flex items-center justify-between flex-wrap gap-2">
          <span className="font-mono text-[10px] md:text-xs uppercase tracking-widest">
            Keep this tab open · flashes run in your browser
          </span>
          <span className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-[#E5FF00]">
            v1.1 · brutalist edition
          </span>
        </div>
      </main>

      <InstallPWABanner />

      {activeFlash && (
        <FlashOverlay
          message={activeFlash.message}
          alarmTone={activeFlash.alarmTone}
          displayMode={activeFlash.displayMode}
          textSize={activeFlash.textSize}
          bgColors={activeFlash.bgColors}
          quietActive={activeFlash.quietActive}
          onStop={handleStopFlash}
          onSnooze={handleSnoozeFlash}
          snoozeCount={snoozeCountsRef.current[activeFlash.scheduleId] || 0}
          snoozeLimit={3}
        />
      )}

      {editingSchedule && (
        <EditScheduleDialog
          schedule={editingSchedule}
          onClose={() => setEditingSchedule(null)}
          onSaved={refresh}
        />
      )}

      <ImportFlashDialog onImported={refresh} />

      {showShareApp && (
        <ShareAppDialog onClose={() => setShowShareApp(false)} />
      )}

      {schedulingTemplate && (
        <ScheduleFromTemplateDialog
          template={schedulingTemplate}
          onClose={() => setSchedulingTemplate(null)}
          onScheduled={refresh}
        />
      )}

      {showQuiet && (
        <QuietHoursDialog
          preferences={preferences}
          onClose={() => setShowQuiet(false)}
          onSaved={(next) => setPreferences(next)}
        />
      )}

      {showCalendar && (
        <CalendarSyncDialog
          preferences={preferences}
          templates={templates}
          onClose={() => setShowCalendar(false)}
          onSaved={(next) => {
            if (next) setPreferences(next);
            else refreshPreferences();
          }}
          onSynced={refresh}
        />
      )}

      <WelcomeTour />
    </div>
  );
}
